import { Group } from "../Core/Group";
import { Element } from "./Element";
import { DomWrapper } from "../Wrapper/DomWrapper";
import { CanvasLayer } from "./CanvasLayer";
import type { IDomWrapable } from "../../interfaces/IDomWrapable";

export type ContainerOptions = {
    id?: string; // 容器 ID
    dom?: HTMLElement; // DOM 元素
    overflowable?: boolean // 是否允许子元素溢出本容器的边界
    zoomFactorBase?: number; // 基础缩放因子
}

export enum ContainerEvents {
    RESIZE = 'resize', // 容器尺寸变化事件
}

/**
 * DOM 容器类，负责管理与实际 DOM 元素关联的可视化容器
 * 
 * 此类提供了:
 * - DOM 元素的尺寸监听和自动调整
 * - 容器附加到 DOM 和从 DOM 分离的功能
 * - Canvas 层的创建和管理，用于绘制背景等元素
 * - 子元素管理及渲染控制
 * 
 * 继承自 Group 类，可以包含和管理多个子元素
 */
export class Container extends Group {
    // 添加一个标记表示DOM是否为外部传入
    private _isDomExternallyProvided: boolean = false;
    // 标记容器是否已附加到 DOM
    private _isAppended: boolean = false;

    // 容器宽度
    private _width: number;
    // 容器高度
    private _height: number;
    // DOM 包装器，用于操作实际的 DOM 元素
    private _target: IDomWrapable;
    // 响应式尺寸观察器，监听容器尺寸变化
    private _resizeObserver!: ResizeObserver;
    // Canvas 渲染层，用于绘制背景等不需要 DOM 元素的内容
    private _canvasLayer?: CanvasLayer;
    // 存储进入全屏前的内联背景样式
    private _preFullscreenInlineBg: string | null = null;
    // 存储进入模拟全屏前的样式
    private _prePseudoFullscreenStyles?: {
        position: string;
        top: string;
        left: string;
        width: string;
        height: string;
        zIndex: string;
        backgroundColor: string;
    };
    /** 获取容器宽度 */
    get width(): number { return this._width; }
    /** 获取容器高度 */
    get height(): number { return this._height; }
    /** 获取 DOM 包装器 */
    get target(): IDomWrapable { return this._target; }

    /**
     * 创建一个容器实例
     * @param options 容器配置对象，可包含DOM元素和其他配置选项
     */
    constructor(options?: ContainerOptions) {

        const { dom, id, overflowable, zoomFactorBase } = options || {};

        let initialId: string | undefined = undefined;
        let containerDom: HTMLElement;

        // 确定使用的DOM元素
        if (dom) {
            containerDom = dom;
            // 如果DOM元素已有ID且options中没有指定id，则使用DOM元素的ID
            if (containerDom.id && !id) {
                initialId = containerDom.id;
            }
        } else {
            // 没有提供DOM元素，创建新的div
            containerDom = document.createElement('div');
        }

        // 从options中获取ID
        if (id) {
            initialId = id;
        }

        super({ id: initialId, zoomFactorBase }); // 调用 Group (Node) 构造函数，传递 id

        // 设置尺寸
        if (dom) {
            this._width = containerDom.offsetWidth;
            this._height = containerDom.offsetHeight;
        } else {
            this._width = 0;
            this._height = 0;
        }

        // 设置DOM来源标记
        this._isDomExternallyProvided = !!dom;

        // 设置容器是否允许子元素溢出
        this.overflowable = overflowable ?? false;

        // 创建DOM包装器
        this._target = new DomWrapper(this, containerDom);

        // 创建尺寸观察器
        this._setupResizeObserver();
    }

    /**
     * 创建并启动尺寸观察器，用于监听容器大小变化
     */
    private _setupResizeObserver(): void {
        // 创建尺寸观察器
        this._resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.target === this.target.htmlEle) {
                    if (
                        this.width === entry.contentRect.width &&
                        this.height === entry.contentRect.height
                    ) {
                        // 尺寸没有变化，直接返回
                        return;
                    }

                    // 更新容器尺寸
                    this._width = entry.contentRect.width;
                    this._height = entry.contentRect.height;

                    this.emit(ContainerEvents.RESIZE, {
                        width: this._width,
                        height: this._height
                    });

                    // 更新Canvas层大小
                    this.updateCanvasSize();
                }
            }
        });

        // 开始观察DOM元素
        this._resizeObserver.observe(this.target.htmlEle);
    }

    /**
     * 停止尺寸观察器，释放相关资源
     */
    private _stopResizeObserver(): void {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
        }
    }

    /**
     * 更新容器在 DOM 中的变换（位置、旋转、缩放）
     */
    protected updateTargetTransform(): void {
        // 无论节点是否有父节点，其自身的局部变换都应该应用到其 DOM 元素上。
        // DOM 元素的 transform 是相对于其 CSS 定位上下文的。
        // 对于根容器，这是相对于其在文档流中的初始位置。
        // 对于子容器，这是相对于其父容器（如果父容器有 transform）。

        // const { x, y } = this.globalPosition;
        // const rotation = this.globalRotation;
        // const { x: scaleX, y: scaleY } = this.globalScaling;

        const { x, y } = this.position;
        const rotation = this.rotation;
        const { x: scaleX, y: scaleY } = this.scaling;

        this.target?.moveTo(x, y);
        this.target?.rotateTo(rotation);
        this.target?.scaleTo(scaleX, scaleY);
    }

    /**
     * 更新容器定位样式
     */
    protected updateDomPositioningStyle(): void {
        if (this.target && this.target instanceof DomWrapper) {
            this.target.updatePositioningStyle();
        }
    }

    /**
     * 渲染容器及其所有子元素
     * 会将容器附加到 DOM 并更新变换
     */
    override render(): void {
        this.attachToDOM();
        this.updateTargetTransform();
    }

    /**
     * 从容器中移除子元素
     * @param child 要移除的元素
     * @returns 当前容器实例，允许链式调用
     */
    override removeChild(child: Element): this {
        const index = this._children.indexOf(child);
        if (index >= 0) {
            // 检查是否为Element类型并调用detachFromDOM
            if (child instanceof Element) {
                child.detachFromDOM();
            }

            this._children.splice(index, 1);
            child.parent = undefined; // 移除父节点引用
        }
        return this;
    }

    /**
     * 更新元素的层级，更新 DOM 中的 z-index
     */
    override updateLevel() {
        super.updateLevel();
        this.target.setLevel(this.level);
    }

    /**
     * 清除所有子元素，并释放它们占用的资源
     */
    override clear(): void {
        for (const child of this._children) {

            if (child instanceof Element) {
                child.dispose();
            } else if (child instanceof Container) {
                child.dispose();
            }
        }
        this._children = [];
    }

    /**
     * 释放容器占用的资源，包括事件监听、观察器和画布层
     * @param forceDetach 是否强制从DOM中分离，即使DOM是外部提供的
     */
    dispose(forceDetach: boolean = false): void {
        // 清除所有子元素
        this.clear();
        // 移除所有事件监听器
        this.removeAllListeners();
        // 停止尺寸观察
        this._stopResizeObserver();
        // 移除Canvas层
        this.removeCanvasLayer();
        // 只有在DOM不是外部提供的，或者明确要求强制分离时才分离
        if (!this._isDomExternallyProvided || forceDetach) {
            this.detachFromDOM();
        }
    }

    /**
     * 设置容器宽度
     * @param value 新的宽度值
     */
    setWidth(value: number) {
        this.setSize({ width: value });
    }

    /**
     * 设置容器高度
     * @param value 新的高度值
     */
    setHeight(value: number) {
        this.setSize({ height: value });
    }

    /**
     * 设置容器尺寸
     * @param size 包含宽度和/或高度的对象
     */
    setSize(size: { width?: number; height?: number; }): void {
        const { width, height } = size;

        if (width !== undefined) {
            this._width = width;
            this.target.setWidth(width);
            this.updateCanvasSize();
        }
        if (height !== undefined) {
            this._height = height;
            this.target.setHeight(height);
            this.updateCanvasSize();
        }
    }

    /**
     * 绘制网格背景
     * @param gridSize 网格大小，默认为 50px
     * @param options 网格样式选项
     * @param options.lineColor 网格线颜色
     * @param options.lineWidth 网格线宽度
     */
    drawGridBackground(
        gridSize: number = 50,
        options: {
            lineColor?: string,
            lineWidth?: number
        } = {}
    ): void {
        // 设置默认选项
        const {
            lineColor = '#e0e0e0',
            lineWidth = 1
        } = options;

        // 获取或创建 Canvas 上下文
        const ctx = this.createCanvasLayer();

        // 清除之前的内容
        ctx.clearRect(0, 0, this.width, this.height);

        // 开始绘制网格
        ctx.beginPath();
        ctx.strokeStyle = lineColor;
        ctx.lineWidth = lineWidth;

        // 绘制垂直线
        for (let x = 0; x <= this.width; x += gridSize) {
            ctx.moveTo(x, 0);
            ctx.lineTo(x, this.height);
        }

        // 绘制水平线
        for (let y = 0; y <= this.height; y += gridSize) {
            ctx.moveTo(0, y);
            ctx.lineTo(this.width, y);
        }

        // 应用描边
        ctx.stroke();
    }

    /**
     * 创建或获取 Canvas 渲染层
     * @returns Canvas 2D 渲染上下文
     */
    createCanvasLayer(): CanvasRenderingContext2D {
        if (!this._canvasLayer) {
            this._canvasLayer = new CanvasLayer(this.target.htmlEle, this.width, this.height);
        }
        return this._canvasLayer.context;
    }

    /**
     * 移除 Canvas 渲染层
     */
    removeCanvasLayer(): void {
        if (this._canvasLayer) {
            this._canvasLayer.dispose();
            this._canvasLayer = undefined;
        }
    }

    /**
     * 更新 Canvas 层尺寸以匹配容器
     */
    updateCanvasSize(): void {
        if (this._canvasLayer) {
            this._canvasLayer.resize(this.width, this.height, () => {
                if (this._canvasLayer) {
                    this._canvasLayer.clear();
                    this.drawGridBackground(); // 重新绘制网格背景
                }
            });
        }
    }

    /**
     * 将容器附加到父 DOM 元素
     * @returns 如果操作导致 DOM 变化则返回 true
     */
    attachToDOM(): boolean {
        // 先更新定位样式，再附加到DOM
        this.updateDomPositioningStyle();

        if (!this._isAppended && this.parent) {
            const dom = this.target.htmlEle;
            const parentDom = this.parent.target.htmlEle;

            if (!parentDom.contains(dom)) {
                parentDom.appendChild(dom);
                this._isAppended = true;
                return true;
            }
        }
        return false;
    }

    /**
     * 从父 DOM 元素中分离容器
     * @returns 如果操作导致 DOM 变化则返回 true
     */
    detachFromDOM(): boolean {
        if (this._isAppended && this.target.htmlEle.parentElement) {
            this.target.htmlEle.parentElement.removeChild(this.target.htmlEle);
            this._isAppended = false;
            return true;
        }
        return false;
    }

    /**
     * 应用全屏背景色
     * 为全屏模式设置合适的背景色，避免透明背景在全屏时显示为黑色
     * @param fallbackColor 当原始背景色为透明时使用的备用颜色，默认为白色
     */
    applyFullscreenBackground(fallbackColor: string = '#ffffff'): void {
        const targetElement = this.target.htmlEle;

        // 1. 保存进入全屏前的内联背景样式
        this._preFullscreenInlineBg = targetElement.style.backgroundColor;

        // 2. 获取当前计算出的背景色来判断是否透明
        const currentComputedBgColor = window.getComputedStyle(targetElement).backgroundColor;
        const isTransparent = this._isColorTransparent(currentComputedBgColor);

        // 3. 如果透明，则应用备用颜色
        if (isTransparent) {
            targetElement.style.backgroundColor = fallbackColor;
            console.log(`应用全屏背景色: ${fallbackColor} (原始: ${currentComputedBgColor})`);
        } else {
            // 如果当前背景色已经不透明，为保险起见，也显式设置它
            // 以防止全屏模式有奇怪的默认值（比如黑色）
            targetElement.style.backgroundColor = currentComputedBgColor;
            console.log(`保持当前背景色: ${currentComputedBgColor}`);
        }
    }

    /**
     * 恢复原始背景色
     * 将容器的背景色恢复到进入全屏之前的状态
     */
    restoreOriginalBackground(): void {
        const targetElement = this.target.htmlEle;

        // 仅当存在保存的进入全屏前的样式时才恢复
        if (this._preFullscreenInlineBg !== null) {
            // 恢复到进入全屏前的内联样式
            targetElement.style.backgroundColor = this._preFullscreenInlineBg;
            console.log(`恢复背景色内联样式为: '${this._preFullscreenInlineBg}'`);
            // 重置状态，避免重复恢复
            this._preFullscreenInlineBg = null;
        }
    }

    /**
     * 应用模拟全屏样式
     * @param backgroundColor 应用于模拟全屏的背景色
     */
    applyPseudoFullscreenStyles(backgroundColor: string): void {
        const element = this.target.htmlEle;

        // 保存原始样式
        this._prePseudoFullscreenStyles = {
            position: element.style.position,
            top: element.style.top,
            left: element.style.left,
            width: element.style.width,
            height: element.style.height,
            zIndex: element.style.zIndex,
            backgroundColor: element.style.backgroundColor,
        };

        // 应用全屏样式
        element.style.position = 'fixed';
        element.style.top = '0';
        element.style.left = '0';
        element.style.width = '100vw';
        element.style.height = '100vh';
        element.style.zIndex = '9999';
        element.style.backgroundColor = backgroundColor;
    }

    /**
     * 恢复模拟全屏前的样式
     */
    restoreOriginalStyles(): void {
        if (!this._prePseudoFullscreenStyles) return;

        const element = this.target.htmlEle;

        // 恢复原始样式
        element.style.position = this._prePseudoFullscreenStyles.position;
        element.style.top = this._prePseudoFullscreenStyles.top;
        element.style.left = this._prePseudoFullscreenStyles.left;
        element.style.width = this._prePseudoFullscreenStyles.width;
        element.style.height = this._prePseudoFullscreenStyles.height;
        element.style.zIndex = this._prePseudoFullscreenStyles.zIndex;
        element.style.backgroundColor = this._prePseudoFullscreenStyles.backgroundColor;

        this._prePseudoFullscreenStyles = undefined;
    }

    /**
     * 检查给定的 CSS 颜色值是否为透明
     * 
     * 该方法支持以下颜色格式的透明度检查：
     * - 关键字颜色值（如 'transparent', 'initial', 'inherit'）
     * - rgba 格式（如 'rgba(255, 255, 255, 0.5)'）
     * - hsla 格式（如 'hsla(120, 100%, 50%, 0.3)'）
     * 
     * @param color 要检查的 CSS 颜色值
     * @returns 如果颜色为透明则返回 true，否则返回 false
     */
    private _isColorTransparent(color: string): boolean {
        if (!color || color === 'transparent' || color === 'initial' || color === 'inherit') {
            return true;
        }

        // 检查 rgba 格式的透明度
        const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
        if (rgbaMatch) {
            const alpha = rgbaMatch[4] ? parseFloat(rgbaMatch[4]) : 1;
            return alpha === 0;
        }

        // 检查 hsla 格式的透明度
        const hslaMatch = color.match(/hsla?\([\d.]+,\s*[\d.]+%,\s*[\d.]+%(?:,\s*([\d.]+))?\)/);
        if (hslaMatch) {
            const alpha = hslaMatch[1] ? parseFloat(hslaMatch[1]) : 1;
            return alpha === 0;
        }

        return false;
    }
}